import type { FeedbackFormStatus } from 'src/types/chat';

import { useState, useEffect } from 'react';

import { Box, Stack, ButtonGroup } from '@mui/material';

import { useSubmitFeedback } from 'src/hooks/api/feedback/useSubmitFeedback';
import { PipelineName, PipelineType } from 'src/hooks/api/pipeline/useCreateRun';

import { FEEDBACK } from 'src/utils/constants';

import { MotionContainer } from 'src/components/animate';

import { EFeedback } from 'src/types/chat';

import CopyButton from './CopyButton';
import DislikeButton from './DislikeButton';
import BadFeedbackCard from './BadFeedbackCard';
import { FeedbackContext } from '../context/FeedbackContext';
import { useChatHistoryContext } from '../hooks/useChatHistoryContext';

type Props = {
  content: string;
  messageId: string;
  conversationId: string | null;
  defaultValue?: EFeedback;
};

function FeedbackForm({ content, messageId, conversationId, defaultValue }: Props) {
  const [feedback, setFeedback] = useState<EFeedback | null>(null);

  const [status, setStatus] = useState<FeedbackFormStatus>('INITIAL');

  const { submitFeedback } = useSubmitFeedback();
  const { history, updateFeedback } = useChatHistoryContext();

  useEffect(() => {
    // Handle default state when coming from other pages
    if (defaultValue) {
      setFeedback(defaultValue);
      setStatus('DONE');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFeedbackSubmit = (value: EFeedback) => {
    setFeedback(value);
    updateFeedback({ vote: value, id: messageId });
    setStatus('CLICKED');
  };

  const handleBadFeedbackSubmit = (value?: string) => {
    submitFeedback({
      runId: history.runId || '',
      payload: {
        sentiment: feedback === EFeedback.DISLIKE ? 'NEGATIVE' : 'POSITIVE',
        message: value || '',
        path: `${PipelineName.CHATBOT}_response.results.chatbot_module`,
        pipeline_type: PipelineType.NOT_DEFINED,
      },
    });
    setStatus('SUBMITTED');

    setTimeout(() => {
      setStatus('DONE');
    }, FEEDBACK.ANIMATION_TIME);
  };

  return (
    <FeedbackContext.Provider value={{ onClose: handleBadFeedbackSubmit }}>
      <Stack spacing={2}>
        <Box component="form" name="feedback-form" mt="4px" className="feedback-form">
          <MotionContainer animate>
            <ButtonGroup variant="outlined" aria-label="Feedback button group" color="inherit">
              <DislikeButton onClick={handleFeedbackSubmit} status={status} />
              <CopyButton content={content} />
            </ButtonGroup>
          </MotionContainer>
        </Box>
        {feedback === EFeedback.DISLIKE && status === 'CLICKED' && <BadFeedbackCard />}
      </Stack>
    </FeedbackContext.Provider>
  );
}

export default FeedbackForm;
